import React from 'react';

import FormField from '@amzn/awsui-components-console/form-field';
import DateRangePicker, {
  DateRangePickerProps,
} from '@amzn/awsui-components-console/date-range-picker';

import {
  isValidRange,
  dateDisabledReason,
  isDateEnabled,
} from 'src/common/utils/dateFormatters';

/** Presets shown in the relative range selector. */
const RELATIVE_PRESETS: DateRangePickerProps.RelativeOption[] = [
  { key: 'last-7-days', amount: 7, unit: 'day', type: 'relative' },
  { key: 'last-30-days', amount: 30, unit: 'day', type: 'relative' },
  { key: 'last-90-days', amount: 90, unit: 'day', type: 'relative' },
  { key: 'last-6-months', amount: 6, unit: 'month', type: 'relative' },
];

/** i18n strings for the DateRangePicker. */
const DATE_RANGE_I18N: DateRangePickerProps.I18nStrings = {
  todayAriaLabel: 'Today',
  nextMonthAriaLabel: 'Next month',
  previousMonthAriaLabel: 'Previous month',
  customRelativeRangeDurationLabel: 'Duration',
  customRelativeRangeDurationPlaceholder: 'Enter duration',
  customRelativeRangeOptionLabel: 'Custom range',
  customRelativeRangeOptionDescription: 'Set a custom range in the past',
  customRelativeRangeUnitLabel: 'Unit of time',
  // istanbul ignore next -- Cloudscape i18n callback; invoked internally by DateRangePicker
  formatRelativeRange: /* istanbul ignore next */ (range) =>
    `Last ${range.amount} ${range.unit}${range.amount > 1 ? 's' : ''}`,
  // istanbul ignore next -- Cloudscape i18n callback; invoked internally by DateRangePicker
  formatUnit: /* istanbul ignore next */ (unit, value) =>
    value === 1 ? unit : `${unit}s`,
  dateTimeConstraintText:
    'Range is limited to the last 1 year. Future dates are not allowed.',
  relativeModeTitle: 'Relative range',
  absoluteModeTitle: 'Absolute range',
  relativeRangeSelectionHeading: 'Choose a range',
  startDateLabel: 'Start date',
  endDateLabel: 'End date',
  clearButtonLabel: 'Clear',
  cancelButtonLabel: 'Cancel',
  applyButtonLabel: 'Apply',
};

interface DateRangeFilterProps {
  value: DateRangePickerProps.Value | null;
  onChange: (value: DateRangePickerProps.Value | null) => void;
}

/**
 * Reusable date range filter with 1-year limit validation.
 * Encapsulates presets, i18n strings, and validation logic.
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
}) => (
  <FormField label="Date Range">
    <DateRangePicker
      value={value}
      // istanbul ignore next -- Cloudscape internal event; not triggerable in jsdom
      onChange={
        /* istanbul ignore next */ ({ detail }) => onChange(detail.value)
      }
      dateOnly={true}
      relativeOptions={RELATIVE_PRESETS}
      isValidRange={isValidRange}
      i18nStrings={DATE_RANGE_I18N}
      dateDisabledReason={dateDisabledReason}
      isDateEnabled={isDateEnabled}
      placeholder="Filter by date range"
      rangeSelectorMode="default"
    />
  </FormField>
);

export default DateRangeFilter;
